import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

const viteEnv=(import.meta as unknown as {env?:Record<string,string|undefined>}).env||{};
export const CMS_API_BASE=(viteEnv.VITE_CMS_API_URL||'https://cms.xynysys.com').replace(/\/$/,'');

export async function cmsFetch(path:string,init:RequestInit={}){
  const headers=new Headers(init.headers||{});
  if(init.body && !(init.body instanceof FormData) && !headers.has('Content-Type'))headers.set('Content-Type','application/json');
  return fetch(`${CMS_API_BASE}${path.startsWith('/')?path:`/${path}`}`,{...init,headers});
}

async function json<T>(path:string,fallback:T):Promise<T>{
  try{
    const r=await cmsFetch(path);
    if(!r.ok)throw new Error(`${r.status}`);
    return await r.json() as T;
  }catch(e){
    console.warn(`[CMS] ${path} unavailable`,e);
    return fallback;
  }
}

export type CmsRecord={
  id?:string;slug?:string;title?:string;accent?:string;summary?:string;excerpt?:string;bodyHtml?:string;
  descriptionHtml?:string;challengeHtml?:string;solutionHtml?:string;architectureHtml?:string;resultsHtml?:string;
  category?:string;industry?:string;client?:string;year?:string|number;author?:string;readMinutes?:number;
  department?:string;location?:string;type?:string;quote?:string;name?:string;role?:string;company?:string;
  outcomeMetric?:string;outcomeLabel?:string;outcome?:string;metric?:string;faqHtml?:string;technologies?:string[];deliverables?:string[];tags?:string[];
  featured?:boolean;imageUrl?:string;seoTitle?:string;seoDescription?:string;canonicalUrl?:string;noIndex?:boolean;createdAt?:string;updatedAt?:string;publishedAt?:string;validThrough?:string;[key:string]:unknown;
};

type CmsState={siteContent:any|null;siteSettings:any|null;taxonomy:any|null;services:CmsRecord[];projects:CmsRecord[];insights:CmsRecord[];testimonials:CmsRecord[];jobs:CmsRecord[]};
const empty:CmsState={siteContent:null,siteSettings:null,taxonomy:null,services:[],projects:[],insights:[],testimonials:[],jobs:[]};
const CmsContext=createContext<CmsState>(empty);

export function CmsProvider({children}:{children:ReactNode}){
  const[state,setState]=useState<CmsState>(empty);
  useEffect(()=>{
    let live=true;
    Promise.all([
      json('/api/site-content',null),json('/api/site-settings',null),json('/api/taxonomy',null),
      json<CmsRecord[]>('/api/services',[]),json<CmsRecord[]>('/api/projects',[]),json<CmsRecord[]>('/api/insights',[]),
      json<CmsRecord[]>('/api/testimonials',[]),json<CmsRecord[]>('/api/jobs',[])
    ]).then(([siteContent,siteSettings,taxonomy,services,projects,insights,testimonials,jobs])=>{
      if(live)setState({siteContent,siteSettings,taxonomy,services,projects,insights,testimonials,jobs});
    });
    return()=>{live=false};
  },[]);
  const value=useMemo(()=>state,[state]);
  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
}

export function useCms(){return useContext(CmsContext)}

export function useCmsDetail(kind:'service'|'project'|'insight'|'job',slug?:string){
  const[data,setData]=useState<CmsRecord|null>(null);
  const[loading,setLoading]=useState(Boolean(slug));
  useEffect(()=>{
    let live=true;
    if(!slug){setLoading(false);return}
    const path={service:'services',project:'projects',insight:'insights',job:'jobs'}[kind];
    setLoading(true);
    json<CmsRecord|null>(`/api/${path}/${encodeURIComponent(slug)}`,null).then(x=>{if(live){setData(x);setLoading(false)}});
    return()=>{live=false};
  },[kind,slug]);
  return {data,loading};
}

export function stripHtml(value?:string){
  if(!value)return'';
  if(typeof document==='undefined')return value.replace(/<[^>]+>/g,' ').replace(/\s+/g,' ').trim();
  const el=document.createElement('div');el.innerHTML=value;return (el.textContent||'').replace(/\s+/g,' ').trim();
}
